import { useQuery } from "@apollo/client/react";
import { Loader } from "@components/loader";
import { FormHeaderNavigaton } from "@components/form-header-navigation";
import { GET_APP_CONFIG_FOR_FORM, GET_FORM_META_DATA } from "@queries/index";
import { CSSProperties, FC, useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router";
import { PermissionTypes } from "@archetypes/gql";
import "./studio-layout.scss";
import { useOutletContext } from "react-router-dom";
import { Permission } from "@contexts/permissions.context";
import { useNavigate } from "react-router-dom";
import { useToastContext } from "@contexts/toast";
import { AppData, AppFormConfig } from "@archetypes/app";
import { UFormData } from "@archetypes/form";
import { useTitle } from "@contexts/title.context";
import { AWSUser } from "@archetypes/auth";
import { LayoutSaveProvider } from "@contexts/layout-save.context";
import { useULAppTheme } from "@hooks/use-ul-app-theme";

const StudioLayoutContainer: FC = () => {
  const { formId, appId } = useParams();

  const { setTitle } = useTitle();
  const user = useOutletContext<AWSUser>();

  const navigate = useNavigate();
  const { showToast } = useToastContext();

  const {
    data: appData,
    loading: isAppLoading,
    error: appError,
  } = useQuery<{
    getApp: AppData;
  }>(GET_APP_CONFIG_FOR_FORM, {
    variables: { input: { appId } },
    fetchPolicy: "network-only",
  });

  const {
    data: formData,
    loading: isFormLoading,
    error: formError,
  } = useQuery<{ getForm: UFormData }>(GET_FORM_META_DATA, {
    variables: {
      input: {
        formId,
        appId,
      },
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!appError) {
      return;
    }

    showToast({
      title: "Error",
      content: `Error fetching app details`,
      variant: "danger",
    });
    navigate("/builder/apps");
  }, [appError, navigate, showToast]);

  useEffect(() => {
    if (!formError) {
      return;
    }

    showToast({
      title: "Error",
      content: `Error fetching form details`,
      variant: "danger",
    });
    navigate("/builder/apps");
  }, [formError, navigate, showToast]);

  useEffect(() => {
    if (formData?.getForm) {
      setTitle(formData?.getForm.formName);
    }
  }, [formData, setTitle]);

  const context: AppFormConfig | undefined = useMemo(() => {
    if (!appData?.getApp || !formData?.getForm) {
      return;
    }
    const { _id, appName, appLogo, primaryColor, fontColor, favicon } = appData.getApp;
    const { currentStatus, type } = formData.getForm;
    return {
      _id,
      appName,
      appLogo,
      fontColor,
      primaryColor,
      favicon,
      currentStatus,
      type,
      user,
    };
  }, [appData, formData, user]);

  // Per-app theme exposed as CSS variables so descendants can read
  // var(--ul-app-primary)/var(--ul-app-font) instead of drilling props.
  // Font color is part of the app data, generated once when the app is
  // created/updated.
  const themeStyle = useMemo(() => {
    return {
      "--ul-app-primary": appData?.getApp?.primaryColor,
      "--ul-app-font": appData?.getApp?.fontColor,
    } as CSSProperties;
  }, [appData]);

  useULAppTheme({
    "--ul-app-primary": appData?.getApp?.primaryColor,
    "--ul-app-font": appData?.getApp?.fontColor,
  });

  if (isAppLoading || isFormLoading) {
    return <Loader />;
  }

  return (
    <LayoutSaveProvider>
      <div className="playground-header">
        <FormHeaderNavigaton
          formName={formData?.getForm?.formName}
          actions={formData?.getForm?.currentAction?.action}
        />
      </div>
      <div className="playground-body" style={themeStyle}>
        <Outlet context={context} />
      </div>
    </LayoutSaveProvider>
  );
};

const StudioLayout: FC = () => {
  return (
    <Permission access={[PermissionTypes.FORM_READ]} redirectUrl="/builder/settings/profile">
      <StudioLayoutContainer />
    </Permission>
  );
};

export default StudioLayout;
